<!-- Detail page for a Decoder.

It is composed differently  from a number of components based on the
Decoder ctype (radio, tv or touch).

-->

<template>
  <div>
    <navbar :links="links" />
    <v-list v-if="decoder.id">
      <decoder-detail-header
        :decoder="decoder"
        :entries="entries"
        :show-demo-menu-store-hint="
          decoder.store.is_demo && decoder.store.is_menu_store
        "
        @toggle-timing="entrySelectionOverlay.toggle('timing')"
        @toggle-removing="entrySelectionOverlay.toggle('removing')"
        @toggle-reactivating="entrySelectionOverlay.toggle('reactivating')"
        @rename="startRenamingDecoder()"
        @tags-changed="onDecoderTagsChanged"
      />
      <tv-decoder-logo-image-preview
        v-if="decoder.id && decoder.ctype === 'tv' && decoder.logo_url"
        :tvdecoder="decoder"
        class="mt-2"
      />
      <radio-decoder-silenced-warning
        v-if="decoder.id && decoder.ctype === 'radio' && decoder.is_silenced"
      />
      <!-- For RadioDecoder: Display/change Channel -->
      <radio-channel-list-tile
        v-if="decoder.id && decoder.ctype === 'radio' && decoder.channel"
        :busy="channelUpdateInProgress"
        :disable-dropdown="decoder.read_only"
        :channel.sync="decoder.channel"
        :old-channel.sync="decoder.oldchannel"
        @selected="
          (channel) => updateChannelOnRadioDecoder(decoder.id, channel)
        "
      />
    </v-list>
    <!-- Loading indicator -->
    <div class="loading-indicator">
      <v-progress-circular
        v-if="!decoder.id && loading"
        :size="50"
        indeterminate
        class="my-5 msr--text"
      />
    </div>
    <!-- Selected Timeframe Filter -->
    <selected-timeframe-chip v-if="decoder.id" />
    <!-- For TouchDecoder: Edit the webpage -->
    <touch-decoder-webpage
      v-if="decoder.ctype === 'touch'"
      :decoder="decoder"
      class="mt-2"
      @changed="fetchDecoder()"
    />
    <!-- For Radio- and TVDecoders: Entries CRUD -->
    <div v-if="decoderSupportsEntries">
      <!-- Current-Menu Header / Preview, including Highlight-Clips -->
      <template v-if="decoder.is_menu_player">
        <list-header
          :label="
            $vuetify.breakpoint.smAndUp
              ? $t('menuTVDecoderCurrentMenuPreview')
              : ''
          "
        />
        <v-layout
          class="mt-2"
          :justify-center="
            !!currentMenu &&
              currentMenu.menu_pages.length +
              currentMenu.menu_item_highlights.length <=
              3
          "
          wrap
        >
          <!-- Menu Pages -->
          <router-link
            v-for="(menuPage, menuPageIndex) in (currentMenu || {})
              .menu_pages || []"
            :key="menuPageIndex"
            :title="$t('menuTVDecoderStoreMenuLink')"
            :to="{name: 'storemenu', params: {storeId: decoder.store.id}}"
          >
            <tv-decoder-menu-page-preview
              :tvdecoder="decoder"
              :menu-page="menuPage"
              class="unselectable mr-3 mb-3"
              :preview-max-width="menuPagePreviewDimensions.maxWidth"
              :preview-max-height="menuPagePreviewDimensions.maxHeight"
            />
          </router-link>
          <!-- Highlight-Clips -->
          <template v-if="!!currentMenu && currentMenu.menu_item_highlights">
            <router-link
              v-for="(
                highlightPayload, highlightPayloadIndex
              ) in currentMenu.menu_item_highlights"
              :key="'menu-item-highlight-preview-' + highlightPayloadIndex"
              :to="{
                name: 'menuitems',
                params: {menuId: decoder.store.menu_id},
                query: {
                  category: highlightPayload.menu_item.category.id,
                  item: highlightPayload.menu_item.id,
                },
              }"
              :title="
                $t('menuItemHighlightDialogTitlePrefix') +
                  ': ' +
                  highlightPayload.menu_item.name
              "
            >
              <tv-template-preview
                ref="highlight-previews"
                hide-player-controls
                class="unselectable mr-3 mb-3 elevation-3"
                :html-overlay="highlightPayload.menu_template.html_overlay"
                :width="highlightPayload.menu_template.tvformat.width"
                :height="highlightPayload.menu_template.tvformat.height"
                :max-width="menuPagePreviewDimensions.maxWidth"
                :max-height="menuPagePreviewDimensions.maxHeight"
                :duration="highlightPayload.menu_template.duration"
                :onload-callback="
                  () =>
                    populateHighlightPreviewIframeWithOverlayData(
                      highlightPayload,
                      highlightPayloadIndex
                    )
                "
              />
            </router-link>
          </template>
          <div class="full-width text-xs-center mb-2">
            <span
              v-if="
                !!currentMenu &&
                  currentMenu.menu_pages.length == 0 &&
                  currentMenu.menu_item_highlights.length == 0
              "
            >{{ $t('tvdecoderMenuPreviewNotPossible') }}.</span>
            <router-link
              class="mx-1"
              :to="{name: 'storemenu', params: {storeId: decoder.store.id}}"
            >
              <span
                v-if="decoder.allowed_menu_categories.length == 0"
                class="warning--text subheading"
                :title="$t('menuTVDecoderStoreMenuLink')"
              >{{ $t('storeMenuAssignAtLeastOneCategoryToPlayer') }}</span>
              <span v-else>{{ $t('menuTVDecoderStoreMenuLink') }}</span>
            </router-link>
          </div>
        </v-layout>
      </template>
      <!-- 'No entries' -->
      <no-entries-header
        :show="!!decoder.id && !activeContent.length && !futureContent.length"
        :label="
          decoder.is_menu_player
            ? $t('noDecoderEntriesForMenuPlayer')
            : $t('noDecoderEntries')
        "
        :show-activation-link="
          !!decoder.id && !decoder.read_only && !entrySelectionOverlay.active
        "
        :on-link-clicked="() => goToAddSpotToDecoder(decoder.ctype, decoder.id)"
        :content-class="decoder.is_menu_player ? 'transparent' : ''"
      />
      <!-- Active Content Header -->
      <detail-entries-header
        v-if="!decoder.is_silenced && activeContent.length"
        :prefix1="
          decoder.is_menu_player
            ? $t('activeContentForMenuPlayer')
            : $t('activeContent')
        "
        :num1="activeContentWithoutDeactivatedTaggedContent.length"
        :prefix2="$t('deactivated')"
        :num2="deactivatedTaggedContent.length"
      />
      <!-- Active Content -->
      <span
        v-for="(entry, index) in activeContent"
        :key="entry.id"
      >
        <spot-entry
          v-if="isSpotEntry(entry) || isTagEntry(entry)"
          :entry="entry"
          :all-entries="entries"
          :ctype="decoder.ctype"
          :update-allowed="!decoder.read_only && !entry.spot.expired"
          :removal-allowed="!decoder.read_only"
          :show-divider="index !== activeContent.length - 1"
          :show-selection-checkbox="entrySelectionOverlay.active"
          :show-actions="!decoder.read_only"
          :show-timing="!entry.spot.closingtime"
          @deleted="fetchDecoder()"
          @changed="fetchDecoder()"
        />
        <ticker-entry
          v-else-if="isTickerEntry(entry)"
          :entry="entry"
          :removal-allowed="!decoder.read_only"
          :show-selection-checkbox="entrySelectionOverlay.active"
          :show-actions="!decoder.read_only"
          :show-divider="index !== activeContent.length - 1"
          @deleted="fetchDecoder()"
          @changed="fetchDecoder()"
        />
      </span>
      <!-- Future Content Header -->
      <detail-entries-header
        v-if="!decoder.is_silenced && futureContent.length"
        :prefix1="$t('futureContent')"
        :num1="futureContent.length"
      />
      <!-- Future Content -->
      <span
        v-for="(entry, index) in futureContent"
        :key="entry.id"
      >
        <spot-entry
          v-if="isSpotEntry(entry)"
          :entry="entry"
          :all-entries="entries"
          :ctype="decoder.ctype"
          :update-allowed="!decoder.read_only && !entry.spot.expired"
          :removal-allowed="!decoder.read_only"
          :show-divider="index !== futureContent.length - 1"
          :show-selection-checkbox="entrySelectionOverlay.active"
          :show-actions="!decoder.read_only"
          :show-timing="!entry.spot.closingtime"
          @deleted="fetchDecoder()"
          @changed="fetchDecoder()"
        />
        <ticker-entry
          v-else-if="isTickerEntry(entry)"
          :entry="entry"
          :removal-allowed="!decoder.read_only"
          :show-selection-checkbox="entrySelectionOverlay.active"
          :show-divider="index !== futureContent.length - 1"
          @deleted="fetchDecoder()"
          @changed="fetchDecoder()"
        />
      </span>
      <!-- Create and activate Ticker button -->
      <v-slide-x-reverse-transition>
        <create-ticker-button
          v-if="showCreateTickerButton"
          :adapt-to-small-screen="false"
          @click="showCreateTickerDialog = true"
        />
      </v-slide-x-reverse-transition>
      <!-- Add Spot to Decoder button -->
      <v-slide-y-reverse-transition>
        <v-btn
          v-if="
            decoder.id &&
              !decoder.is_silenced &&
              !decoder.read_only &&
              !entrySelectionOverlay.active
          "
          :key="decoder.id"
          class="msr"
          :class="$desktopModeStyles"
          fixed
          right
          bottom
          dark
          round
          data-cy="add-spot-to-decoder-btn"
          @click="goToAddSpotToDecoder(decoder.ctype, decoder.id)"
        >
          <v-icon left>
            add
          </v-icon>
          {{ $t('addContentToDecoder') }}
        </v-btn>
      </v-slide-y-reverse-transition>
      <!-- Empty placeholder to allow a bit more scrolling -->
      <div style="height: 100px" />
    </div>
    <!-- Entry Selection Overlay for Batch Timing -->
    <entry-selection-overlay
      v-if="entries"
      :show="entrySelectionOverlay.active"
      :entries="entries"
      :mode="entrySelectionOverlay.mode"
      :title="entrySelectionOverlay.title"
      :title-icon="entrySelectionOverlay.titleIcon"
      :loading="loading"
      @aborted="entrySelectionOverlay.stop()"
      @confirmed="onEntrySelectionOverlayConfirmed()"
    />
    <!-- Entry Timing Dialog -->
    <entry-timing-dialog
      :show.sync="showEntryTimingDialog"
      :entries="selectedEntries"
      :show-timeframe-controls="!onlyClosingtimeRadioSpotEntriesSelected"
      @submitted="
        entrySelectionOverlay.stop();
        fetchDecoder();
      "
    />
    <!-- Rename Decoder Dialog -->
    <v-dialog
      v-model="showRenameDecoderDialog"
      persistent
      lazy
      :width="360"
    >
      <v-card>
        <v-card-title class="pb-0">
          <div class="headline">
            {{ $t('renameDecoder') }}
          </div>
          <v-btn
            class="ml-auto mr-0 my-0"
            flat
            icon
            :title="$t('close')"
            @click="abortRenamingDecoder()"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="py-0">
          <v-text-field
            ref="decoderDescriptionInput"
            v-model="newDecoderDescription"
            :counter="decoderDescriptionMaximum"
            :rules="[
              decoderDescriptionRules.min,
              decoderDescriptionRules.max,
              decoderDescriptionRules.unique,
            ]"
            class="mb-1"
            autofocus
            data-cy="rename-decoder-input"
            @keyup.esc="abortRenamingDecoder()"
            @keyup.enter="onRenamedDecoderSubmitted()"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <btn-ok
            data-cy="rename-decoder-confirm-btn"
            :disabled="!renameFormIsValid"
            :loading="renaming"
            round
            @click="onRenamedDecoderSubmitted()"
          />
          <v-spacer />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Create Ticker Dialog -->
    <create-ticker-dialog
      v-if="showCreateTickerButton"
      ref="createTickerDialog"
      :show="showCreateTickerDialog"
      :uploadgroups="$store.getters.uploadgroups"
      @toggled="(val) => (showCreateTickerDialog = val)"
      @submitted="createTickerAndGoToEntryCreationDialog"
      @aborted="abortTickerCreation()"
    />
  </div>
</template>

<script>
import {mapState} from 'vuex';
import debounce from 'lodash.debounce';

import {deepCopyObject, getMainTVSpotFromBundle} from '@/utils';
import commonEntry from '@/mixins/commonEntry.js';
import entrySelectionOverlay from '@/mixins/entrySelectionOverlay.js';
import identifiers from '@/mixins/identifiers.js';
import renameDecoder from '@/mixins/renameDecoder.js';
import selectedTimeframes from '@/mixins/selectedTimeframes.js';

import BtnOk from '@/components/ButtonOk';
import CreateTickerButton from '@/components/CreateTickerButton';
import CreateTickerDialog from '@/components/CreateTickerDialog';
import DecoderDetailHeader from '@/components/DecoderDetail/DecoderDetailHeader';
import DetailEntriesHeader from '@/components/DetailEntriesHeader';
import EntrySelectionOverlay from '@/components/EntrySelectionOverlay';
import EntryTimingDialog from '@/components/EntryTimingDialog';
import ListHeader from '@/components/ListHeader';
import Navbar from '@/components/Navbar';
import NoEntriesHeader from '@/components/NoEntriesHeader';
import RadioChannelListTile from '@/components/RadioChannelListTile';
import RadioDecoderSilencedWarning from '@/components/DecoderDetail/RadioDecoderSilencedWarning';
import SelectedTimeframeChip from '@/components/SelectedTimeframeChip';
import SpotEntry from '@/components/SpotEntry';
import TickerEntry from '@/components/TickerEntry';
import TouchDecoderWebpage from '@/components/DecoderDetail/TouchDecoderWebpage';
import TvDecoderLogoImagePreview from '@/components/DecoderDetail/TvDecoderLogoImagePreview';
import TvDecoderMenuPagePreview from '@/components/DecoderDetail/TvDecoderMenuPagePreview';
import TvTemplatePreview from '@/components/TvTemplatePreview';

export default {
  components: {
    BtnOk,
    CreateTickerButton,
    CreateTickerDialog,
    DecoderDetailHeader,
    DetailEntriesHeader,
    EntrySelectionOverlay,
    EntryTimingDialog,
    ListHeader,
    Navbar,
    NoEntriesHeader,
    RadioChannelListTile,
    RadioDecoderSilencedWarning,
    SelectedTimeframeChip,
    SpotEntry,
    TickerEntry,
    TouchDecoderWebpage,
    TvDecoderLogoImagePreview,
    TvDecoderMenuPagePreview,
    TvTemplatePreview,
  },
  mixins: [
    commonEntry,
    entrySelectionOverlay,
    identifiers,
    renameDecoder,
    selectedTimeframes,
  ],
  data() {
    return {
      loading: false,
      links: ['sites', this.$route.name],
      showEntryTimingDialog: false,
      showCreateTickerDialog: false,
      channelUpdateInProgress: false,
    };
  },
  computed: {
    ...mapState({
      decoder: (state) => state.common.decoder,
    }),
    currentMenu() {
      if (!this.decoder) {
        return null;
      }
      const currentMenu = this.decoder.current_menu;
      if (window.debug) {
        console.log();
        console.log(JSON.stringify(deepCopyObject(currentMenu), null, 2));
        console.log();
      }
      return currentMenu;
    },
    menuPagePreviewDimensions() {
      return {
        maxWidth: 96 * 6,
        maxHeight: 54 * 9.5,
      };
    },
    isAllowedToCreateTickers() {
      return this.$store.getters.userCanCreateTickers;
    },
    showCreateTickerButton() {
      return (
        this.decoder.ctype == 'tv' &&
        this.decoder.ticker &&
        this.isAllowedToCreateTickers
      );
    },
    decoderSupportsEntries() {
      return this.decoder.ctype === 'radio' || this.decoder.ctype === 'tv';
    },
    /**
     * Abstract away spot.entries VS decoder.entries.
     */
    entries() {
      if (this.decoder.is_silenced) {
        return [];
      }
      return this.decoder.entries;
    },
    filteredEntries() {
      return this.entriesForSelectedTimeframes;
    },
    orderedEntries() {
      return this.orderEntriesByPlayAfterEntry(this.filteredEntries);
    },
    futureContent() {
      return this.getFutureEntries(this.orderedEntries);
    },
    activeContent() {
      return this.getActiveEntries(this.orderedEntries);
    },
    activeContentWithoutDeactivatedTaggedContent() {
      return this.activeContent.filter(
        (entry) => !this.isDeactivatedTagEntry(entry)
      );
    },
    deactivatedTaggedContent() {
      return this.getDeactivatedTagEntries(this.orderedEntries);
    },
  },
  created() {
    // We prefer to start with an empty page if the cached Decoder in
    // the store is different from the one want to look at.
    const cacheValid =
      this.decoder.id !== undefined &&
      this.decoder.ctype == this.$route.params.ctype &&
      this.decoder.id == this.$route.params.decoderId;
    if (!cacheValid) {
      this.$store.commit('RESET_DECODER');
    }

    // Don't show spinner when simply refreshing current Decoder, only
    // on initial load or when fetching a different one.
    this.loading = this.decoder.id === undefined;
    this.fetchDecoder().then(() => {
      this.updateNavbarLinks(this.decoder);
      this.notifyInitialOnlineStatus(this.decoder);
      this.loading = false;
    });

    this.entrySelectionOverlay.view = 'decoder';

    this.$store.dispatch('LIST_RADIO_CHANNELS');
  },
  methods: {
    onEntrySelectionOverlayConfirmed() {
      if (this.entrySelectionOverlay.mode === 'timing') {
        this.showEntryTimingDialog = true;
      } else if (this.entrySelectionOverlay.mode === 'removing') {
        this.batchRemoveEntries(this.selectedEntries);
      } else if (this.entrySelectionOverlay.mode === 'reactivating') {
        this.batchReactivateTagEntries(this.selectedTagEntries);
      }
    },
    populateHighlightPreviewIframeWithOverlayData(
      menuItemHighlightPayload,
      widgetIndex
    ) {
      const previewWidgets = this.$refs['highlight-previews'];
      const previewWidget = previewWidgets[widgetIndex];
      if (!previewWidget) {
        return;
      }
      const overlayData = {
        menu_item_highlight: menuItemHighlightPayload,
      };
      previewWidget.updateOverlayData(overlayData);
    },
    onDecoderTagsChanged: function () {
      this.$store.commit('SET_FORCE_REFETCH_CONTENTS_ON_NEXT_VISIT', true);
      this.$store.commit('RESET_SITES');
      this.fetchDecoderDebounced();
    },
    onRenamedDecoderSubmitted() {
      if (!this.renameFormIsValid) {
        return;
      }
      const onSuccess = this.fetchDecoder;
      this.updateDecoderDescription(
        this.decoder.ctype,
        this.decoder.id,
        this.newDecoderDescription,
        onSuccess
      );
    },
    orderEntriesByPlayAfterEntry(entries) {
      const entriesToReorder = entries.filter(
        (e) => e.play_after_entry != null
      );
      entriesToReorder.forEach((entryToReorder) => {
        for (let i = entries.length - 1; i >= 0; i--) {
          const entry = entries[i];
          // Find child and remove it from list.
          if (entry.id === entryToReorder.id) {
            entries.splice(i, 1);
          }
          // Find parent and insert afterwards.
          if (entry.id === entryToReorder.play_after_entry.id) {
            entries.splice(i + 1, 0, entryToReorder);
          }
        }
      });
      return entries;
    },
    fetchDecoder() {
      return this.$store.dispatch('RETRIEVE_DECODER', {
        ctype: this.$route.params.ctype,
        decoderId: this.$route.params.decoderId,
      });
    },
    fetchDecoderDebounced: debounce(function () {
      this.fetchDecoder();
    }, 1000),
    goToAddSpotToDecoder(type, decoderId) {
      let path = '/player/' + type + '/' + decoderId + '/add';
      this.$router.push(path);
    },
    /**
     * Update last link to reflect the Decoder we are viewing.
     */
    updateNavbarLinks(decoder) {
      let decoderLink = {
        name: this.$route.name,
        label: decoder.store.city + ': ' + decoder.description,
      };
      this.links = ['sites', decoderLink];

      let previous = this.$store.getters.previousRoute;
      if (!previous) {
        return;
      }

      if (previous.name === 'contents') {
        this.links = ['contents', decoderLink];
      } else if (previous.name === 'decoderLink') {
        this.links = ['dashboard', spotLink];
      } else if (previous.name === 'spot') {
        this.$api
          .retrieveContent(previous.params.ctype, previous.params.spotId)
          .then((response) => {
            let spot = response.data;
            if (spot.kind === 'tvspotbundle') {
              spot = getMainTVSpotFromBundle(spot);
            }
            let title = spot.title.substring(0, 18);
            if (title.length < spot.title.length) {
              title += '..';
            }
            const spotLink = {label: title, path: previous.path};
            this.links = ['contents', spotLink, decoderLink];
          });
      }
    },
    notifyInitialOnlineStatus(decoder) {
      if (decoder.is_online === false) {
        this.$bus.$emit('alert-error', {
          message: 'decoderOffline',
          translate: true,
          timeout: 6000,
        });
      }
    },
    batchRemoveEntries(entries) {
      this.loading = true;
      const allowedEntries = null;
      this.batchRemoveMultipleEntries(entries, allowedEntries).then(() => {
        this.loading = false;
        this.entrySelectionOverlay.stop();
        this.fetchDecoder();
      });
    },
    batchReactivateTagEntries(tagEntries) {
      this.loading = true;
      const tagEntryIdents = tagEntries.map((entry) => entry.ident);
      this.$api.reactivateTagEntries(tagEntryIdents).then(() => {
        this.loading = false;
        this.entrySelectionOverlay.stop();
        this.fetchDecoder();
      });
    },
    updateChannelOnRadioDecoder(decoderId, newChannel) {
      this.channelUpdateInProgress = true;
      this.$api
        .activateChannelOnSingleRadioDecoder(newChannel.id, decoderId)
        .then((response) => {
          this.channelUpdateInProgress = false;

          const template = this.$t('radioChannelUpdateSuccess');
          const message = template.replace(
            '{{ channelName }}',
            newChannel.name
          );
          this.$bus.$emit('alert-success', {
            message: message,
            translate: false,
            timeout: 8000,
          });
        });
    },
    // TODO: This is pretty similar to Contents.vue. If this is extended
    //   or used somewhere else we should probably extract and reuse it.
    createTickerAndGoToEntryCreationDialog(message, groupId) {
      const dialog = this.$refs.createTickerDialog;
      dialog.busy = true;
      this.$api.createTicker(message, groupId).then((response) => {
        this.$bus.$emit('alert-success', {
          message: 'tickerCreatedSuccess',
          timeout: 2000,
          translate: true,
        });

        dialog.busy = false;
        dialog.reset();
        this.showCreateTickerDialog = false;

        const ticker = response.data;
        const route = {
          path: `/ticker/${ticker.id}/add`,
          query: {preselect: this.decoder.id},
        };
        this.$router.push(route);
      });
    },
    abortTickerCreation() {
      this.$refs.createTickerDialog.reset();
      this.showCreateTickerDialog = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/DecoderDetail.vue